import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ReactGA from 'react-ga4';

import "./App.css";
import axios from "axios";
import Loader from "./components/Loader";
import LayoutView from "./modules/layout/LayoutView";
import lazyComponentLoader from "./hoc/LazyLoader";
import ProtectedRoute from "./hoc/ProtectedRoute";
import UnprotectedRoute from "./hoc/UnprotectedRoute";
import DialogUtils from "./utils/DialogUtils";
import config from "./config";

// ─── Lazy-loaded routes ───────────────────────────────────────────────────────
const Login = lazyComponentLoader(() => import("./modules/auth/login/Login"));
const Forgot = lazyComponentLoader(() => import("./modules/auth/forgot-password/ForgotPassword"));
const Reset = lazyComponentLoader(() => import("./modules/auth/forgot-password/ResetPassword"));

const SignupView = lazyComponentLoader(() => import("./modules/auth/signup/Signup"));
const DriversRegistrationView = lazyComponentLoader(() => import("./modules/auth/signup/DriverSignup"));

const Home = lazyComponentLoader(() => import("./modules/home/HomeView"));
const NewRestaurantDetailView = lazyComponentLoader(() => import("./modules/restaurant/NewRestaurantDetailView"));
const OrderPickupView = lazyComponentLoader(() => import("./modules/order/OrderPickupView"));
const OrderView = lazyComponentLoader(() => import("./modules/order/OrderView"));
const OrderHistoryView = lazyComponentLoader(() => import("./modules/customer_profile/OrderHistoryView"));
const CustomerProfileView = lazyComponentLoader(() => import("./modules/customer_profile/CustomerProfileView"));
const EditCustomerProfileView = lazyComponentLoader(() => import("./modules/customer_profile/EditCustomerProfileView"));
const ChangePasswordView = lazyComponentLoader(() => import("./modules/customer_profile/ChangePasswordView"));
const StripeCompleteOrderView = lazyComponentLoader(() => import("./modules/order/StripeCompleteOrderView")); // NEED TO RE-CHECK
const RestaurantNewSearchViewNew = lazyComponentLoader(() => import("./modules/restaurant/RestaurantNewSearchViewNew"));

const AboutUsView = lazyComponentLoader(() => import("./modules/about-us/AboutUsView"));
const PrivacyPolicyView = lazyComponentLoader(() => import("./modules/privacy-policy/PrivacyPolicyView"));
const ContactUsView = lazyComponentLoader(() => import("./modules/contact-us/ContactUsView"));

// ─── Error handler ────────────────────────────────────────────────────────────
let deletePopupCtr = 0;

const showError = (errors: any) => {
  let content: any = null;

  // The API's fallback 404 handler returns a bare { message: "Not Found" } with
  // no actionable detail. It briefly surfaces during history/back-navigation
  // races (a page re-mounts and refetches with state that is no longer there),
  // so never raise a blocking dialog for it (AP-5852).
  if (errors && !Array.isArray(errors) && errors.message === "Not Found") {
    return;
  }

  if (Array.isArray(errors)) {
    errors.forEach((err: any) => {
      if (
        err?.context?.key === "refresh_token" ||
        err?.context?.key === "access_token"
      ) {
        DialogUtils.error(
          "You have been logged out. Please login again.",
          () => { localStorage.clear(); }
        );
      } else {
        content = err.message;
      }
    });
  } else if (
    (errors.key === "resource_not_found" || errors.key === "not_found") &&
    errors.resource_type === "voucher"
  ) {
    content = "Promo Code is not available.";
  } else if (errors !== "Unauthorized") {
    content = errors.message?.includes("Network Error")
      ? "No network connection. Make sure that WI-FI or Cellular Mobile Data is turned on, then try again."
      : errors.message;
  }

  if (errors.message === "deleted account" || content === "deleted account") {
    deletePopupCtr += 1;
    if (deletePopupCtr < 2) {
      DialogUtils.error(
        "Your account has been deleted. Please register again.",
        () => {
          deletePopupCtr = 0;
          localStorage.clear();
          window.location.href = "/";
        }
      );
    }
  } else if (content) {
    content =
      content.includes("timeout") ||
        content.includes("access_token") ||
        content.includes("jwt")
        ? "Your session has timed out."
        : content;
    const parts = content.split("Error: ");
    DialogUtils.error(parts[1] ?? content);
  }
};

// ─── App ──────────────────────────────────────────────────────────────────────
const App = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('.ant-pagination .ant-select') &&
          !['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab'].includes(e.key)) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, []);

  useEffect(() => {
    const { gtag } = window as any;
    if (typeof gtag === "function") {
      gtag("js", new Date());
      gtag("config", config.MEASUREMENT_ID);
    }
  }, []);

  useEffect(() => {
    // Count in-flight requests so the loader stays up until the last one finishes — with a plain
    // boolean the first response to come back hid it while parallel requests were still loading.
    let pending = 0;
    const requestStarted  = () => { pending += 1; setIsLoading(true); };
    const requestFinished = () => { pending = Math.max(0, pending - 1); setIsLoading(pending > 0); };

    const requestInterceptor = axios.interceptors.request.use(
      (config) => { requestStarted(); return config; },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => { requestFinished(); return response; },
      async (error) => {
        requestFinished();
        if (error.response) {
          showError(error.response.data.errors ?? error.response.data);
        } else {
          showError(error);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);


  // For Google Analytics 
  ReactGA.initialize(config.MEASUREMENT_ID);

  return (
    <div>
      <Routes>
        <Route element={<LayoutView />}>
          <Route path="/login" element={<UnprotectedRoute element={<Login />} />} />
          <Route path="/forgot-password" element={<UnprotectedRoute element={<Forgot />} />} />
          <Route path="/reset-password" element={<UnprotectedRoute element={<Reset />} />} />

          <Route path="/" element={<Home />} />
          <Route path="/signup" element={<SignupView />} />
          <Route path="/driver-registration" element={<DriversRegistrationView />} />
          <Route path="/restaurants/:id" element={<NewRestaurantDetailView />} />
          <Route path="/restaurants-search" element={<RestaurantNewSearchViewNew />} />
          <Route path="/restaurants" element={<OrderPickupView />} />
          <Route path="/about" element={<AboutUsView />} />
          <Route path="/privacy-notice" element={<PrivacyPolicyView />} />
          <Route path="/contact-us" element={<ContactUsView />} />

          <Route path="/orders" element={<ProtectedRoute element={<OrderView />} />} />
          <Route path="/confirm-purchase" element={<ProtectedRoute element={<StripeCompleteOrderView />} />} />
          <Route path="/order-history" element={<ProtectedRoute element={<OrderHistoryView />} />} />
          <Route path="/profile" element={<ProtectedRoute element={<CustomerProfileView />} />} />
          <Route path="/edit-profile" element={<ProtectedRoute element={<EditCustomerProfileView />} />} />
          <Route path="/change-password" element={<ProtectedRoute element={<ChangePasswordView />} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>

      {isLoading && <Loader />}
    </div>
  );
};

export default App;

